import { useRef, useCallback, useMemo, KeyboardEvent, RefObject } from "react";
import {
  UseEnterNavigationProps,
  UseEnterNavigationReturn,
  FieldProps,
} from "./useEnterNavigation.types";

export function useEnterNavigation({
  fields,
  onSubmit,
}: UseEnterNavigationProps): UseEnterNavigationReturn {
  const refsMap = useRef<Map<string, RefObject<HTMLInputElement | null>>>(
    new Map()
  );

  const getOrCreateRef = useCallback(
    (fieldId: string): RefObject<HTMLInputElement | null> => {
      if (!refsMap.current.has(fieldId)) {
        refsMap.current.set(fieldId, { current: null });
      }
      return refsMap.current.get(fieldId)!;
    },
    []
  );

  const enabledFields = useMemo(
    () => fields.filter((field) => field.enabled !== false),
    [fields]
  );

  const focusField = useCallback((fieldId: string) => {
    const ref = refsMap.current.get(fieldId);
    if (ref?.current) {
      ref.current.focus();
    }
  }, []);

  const getFieldProps = useCallback(
    (fieldId: string): FieldProps => {
      const ref = getOrCreateRef(fieldId);
      const currentIndex = enabledFields.findIndex(
        (field) => field.id === fieldId
      );
      const isLastField = currentIndex === enabledFields.length - 1;

      const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (isLastField) {
            onSubmit?.();
          } else {
            const nextField = enabledFields[currentIndex + 1];
            if (nextField) {
              focusField(nextField.id);
            }
          }
        }
      };

      return { ref, onKeyDown };
    },
    [enabledFields, getOrCreateRef, focusField, onSubmit]
  );

  return { getFieldProps, focusField };
}
